"use client"

import * as React from "react"
import { Link } from "react-router-dom"
import type { LucideIcon } from "lucide-react"

import { cn, Skeleton } from "@ibada/ui"

import { useTranslations } from "@/i18n"

/** How a row's bar reads: still going, reached, or past what it should be (an overspent expense line). */
export enum ProgressRowTone {
  Default = "default",
  Complete = "complete",
  Over = "over",
}

/** The status pill's colour, by meaning rather than by hue. */
export enum ProgressRowPillTone {
  Success = "success",
  Neutral = "neutral",
  Warning = "warning",
  Danger = "danger",
  Info = "info",
}

/** Reached at 100 %, red only when the caller says the row is over (a fuller income line is good news). */
export function progressRowTone(percentage: number, over = false): ProgressRowTone {
  if (over) return ProgressRowTone.Over
  return percentage >= 100 ? ProgressRowTone.Complete : ProgressRowTone.Default
}

/**
 * A list of progress rows (the pledge campaign board's contributor list): one soft card, rows split by
 * dividers. Desktop only; phones keep their own rows.
 */
export function ProgressRowList({
  label,
  busy = false,
  className,
  children,
}: {
  label: string
  /** The rows are placeholders (first load): marked busy for assistive tech. */
  busy?: boolean
  className?: string
  children: React.ReactNode
}) {
  return (
    <div role='table' aria-label={label} aria-busy={busy || undefined} className={cn("bg-card divide-border divide-y overflow-hidden rounded-2xl border", className)}>
      {children}
    </div>
  )
}

/** Placeholder rows while the first page loads. */
export function ProgressRowListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, index) => (
        // The real row's grid: lead (mark, title, subtitle), amount, bar, status pill, actions.
        <div
          key={index}
          aria-hidden
          className='grid grid-cols-[minmax(0,1.6fr)_minmax(0,1.3fr)_minmax(0,1.5fr)_minmax(0,1fr)_auto] items-center gap-4 px-4 py-2.5'
        >
          <span className='flex min-w-0 items-center gap-3'>
            <Skeleton className='size-8 shrink-0 rounded-full' />
            <span className='flex min-w-0 flex-1 flex-col gap-1.5'>
              <Skeleton className={cn("h-3 rounded-full", index % 2 === 0 ? "w-3/5" : "w-2/5")} />
              <Skeleton className='h-2.5 w-1/3 rounded-full' />
            </span>
          </span>
          <Skeleton className='h-3 w-3/5 rounded-full' />
          <Skeleton className='h-2 w-full rounded-full' />
          <Skeleton className='h-6 w-20 rounded-full' />
          <Skeleton className='size-8 rounded-full' />
        </div>
      ))}
    </>
  )
}

/** The line a list shows instead of rows (nothing yet, no match). */
export function ProgressRowListMessage({ children }: { children: React.ReactNode }) {
  return <div className='text-muted-foreground px-4 py-8 text-center text-sm font-semibold'>{children}</div>
}

/** Holds a table's own empty state (an EmptyState) inside the list's card. */
export function ProgressRowListEmpty({ children }: { children: React.ReactNode }) {
  return <div className='px-4 py-6'>{children}</div>
}

/**
 * One row: lead (mark, title, subtitle), amount, bar with its %, status, actions. Pass the five cells as
 * children, in that order; the row opens from its title, whose hit area covers the whole row.
 */
export function ProgressRow({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <div
      role='row'
      className={cn(
        "hover:bg-muted/40 focus-within:bg-muted/40 relative grid grid-cols-[minmax(0,1.6fr)_minmax(0,1.3fr)_minmax(0,1.5fr)_minmax(0,1fr)_auto] items-center gap-4 px-4 py-2.5",
        className,
      )}
    >
      {children}
    </div>
  )
}

/** A round mark for a row that has no person behind it (a fund, a budget line). */
export function ProgressRowMark({ icon: Icon, className }: { icon: LucideIcon; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn("bg-muted text-muted-foreground inline-flex size-8 shrink-0 items-center justify-center rounded-full", className)}
    >
      <Icon className='size-4' />
    </span>
  )
}

const titleClassName =
  "truncate text-left text-[13.5px] font-extrabold after:absolute after:inset-0 focus-visible:outline-none"

/** The leading cell: avatar or mark, the title (a button or a link that opens the row) and a muted subtitle. */
export function ProgressRowLead({
  leading,
  title,
  subtitle,
  onOpen,
  href,
}: {
  leading: React.ReactNode
  title: React.ReactNode
  subtitle?: React.ReactNode
  onOpen?: () => void
  href?: string
}) {
  return (
    <span role='cell' className='flex min-w-0 items-center gap-2.5'>
      {leading}
      <span className='flex min-w-0 flex-col'>
        {href ? (
          <Link to={href} className={titleClassName}>
            {title}
          </Link>
        ) : onOpen ? (
          <button type='button' onClick={onOpen} className={titleClassName}>
            {title}
          </button>
        ) : (
          <span className='truncate text-[13.5px] font-extrabold'>{title}</span>
        )}
        {subtitle ? <span className='text-muted-foreground truncate text-xs font-semibold'>{subtitle}</span> : null}
      </span>
    </span>
  )
}

/** "X of Y": the amount reached in bold, the target muted. Without a target only the amount shows. */
export function ProgressRowAmount({
  value,
  target,
  struck = false,
  children,
}: {
  value?: React.ReactNode
  target?: React.ReactNode
  /** A cancelled row: the figures stay readable but crossed out. */
  struck?: boolean
  /** Replaces the figures (a skeleton or a retry while they load). */
  children?: React.ReactNode
}) {
  const t = useTranslations("common.progressRow")
  return (
    <span role='cell' className={cn("truncate text-[13px] tabular-nums", struck && "text-muted-foreground line-through")}>
      {children ?? (
        <>
          <span className='font-extrabold'>{value}</span>
          {target !== undefined && target !== null ? (
            <>
              {" "}
              <span className='text-muted-foreground'>{t("of", { amount: String(target) })}</span>
            </>
          ) : null}
        </>
      )}
    </span>
  )
}

const BAR_CLASS: Record<ProgressRowTone, string> = {
  [ProgressRowTone.Default]: "bg-primary",
  [ProgressRowTone.Complete]: "bg-teal-600",
  [ProgressRowTone.Over]: "bg-destructive",
}

/**
 * The thin bar and its %. The label shows the real figure (125 % on an overspent line), the bar stops at
 * full. Pass `children` instead of a percentage when the row has nothing to measure against.
 */
export function ProgressRowBar({
  percentage,
  tone,
  children,
}: {
  percentage?: number | null
  tone?: ProgressRowTone
  children?: React.ReactNode
}) {
  if (percentage === undefined || percentage === null || Number.isNaN(percentage)) {
    return (
      <span role='cell' className='text-muted-foreground min-w-0 truncate text-xs font-semibold'>
        {children}
      </span>
    )
  }
  const resolvedTone = tone ?? progressRowTone(percentage)
  const width = Math.max(0, Math.min(100, percentage))
  return (
    <span role='cell' data-tone={resolvedTone} className='flex min-w-0 items-center gap-2'>
      <span className='bg-muted h-2 min-w-0 flex-1 overflow-hidden rounded-full'>
        <span className={cn("block h-full rounded-full", BAR_CLASS[resolvedTone])} style={{ width: `${width}%` }} />
      </span>
      <span
        className={cn(
          "w-10 text-right text-xs font-extrabold tabular-nums",
          resolvedTone === ProgressRowTone.Over && "text-destructive",
        )}
      >
        {Math.round(percentage)} %
      </span>
    </span>
  )
}

/** The status cell: a pill, or a muted line (a due date). */
export function ProgressRowStatus({ children }: { children?: React.ReactNode }) {
  return (
    <span role='cell' className='min-w-0'>
      {children}
    </span>
  )
}

const PILL_CLASS: Record<ProgressRowPillTone, string> = {
  [ProgressRowPillTone.Success]: "bg-teal-50 text-teal-800 dark:bg-teal-500/15 dark:text-teal-300",
  [ProgressRowPillTone.Neutral]: "bg-muted text-muted-foreground",
  [ProgressRowPillTone.Warning]: "bg-amber-50 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300",
  [ProgressRowPillTone.Danger]: "bg-destructive/10 text-destructive",
  [ProgressRowPillTone.Info]: "bg-sky-50 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300",
}

export function ProgressRowPill({ tone, children }: { tone: ProgressRowPillTone; children: React.ReactNode }) {
  return <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-extrabold", PILL_CLASS[tone])}>{children}</span>
}

/** A muted status line, for a row that has no pill to show. */
export function ProgressRowNote({ children }: { children: React.ReactNode }) {
  return <span className='text-muted-foreground text-xs font-semibold'>{children}</span>
}

/** The trailing cell (a ⋯ menu, a quick action). It sits above the row's hit area. Always render it. */
export function ProgressRowActions({ children }: { children?: React.ReactNode }) {
  return (
    <span role='cell' className='relative z-10 flex items-center justify-end gap-1.5'>
      {children}
    </span>
  )
}
